// A stale client (old tab after a deploy) requests a code-split chunk whose
// hashed file no longer exists, and the import fails. Each browser phrases it
// differently, so match on the known messages.
const CHUNK_LOAD_ERROR_PATTERNS = [
  'is not a valid javascript mime type', // Safari: stale chunk served as index.html
  'failed to fetch dynamically imported module', // Chrome / Firefox
  'error loading dynamically imported module', // Chrome / Firefox (variant)
  'importing a module script failed', // Safari: module fetch/parse failure
  'unable to preload css', // Vite: preloaded stylesheet failure
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const normalized = message.toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

const RELOAD_MARKER_KEY = 'enrich:chunk-reload-at';

// Only auto-reload once per window, so a reload that doesn't fix it (offline,
// asset permanently gone) can't loop.
export const RELOAD_DEBOUNCE_MS = 30_000;

// Unlike the sessionStorage marker, module state lasts exactly as long as this
// page, so it tells us whether this page has already asked for a reload.
let reloadRequestedAt: number | undefined;

interface ReloadOptions {
  storage?: Storage;
  now?: number;
  reload?: () => void;
}

// Reloads to recover from a chunk-load error, at most once per debounce window;
// returns whether a reload is underway. Deps are injectable for testing.
export function reloadForChunkError({
  storage = window.sessionStorage,
  now = Date.now(),
  reload = () => window.location.reload(),
}: ReloadOptions = {}): boolean {
  // The `vite:preloadError` listener usually reloads before the router's error
  // component runs. If the page is still open after the window, the reload was
  // cancelled (e.g. a beforeunload prompt), so fall through and retry.
  if (reloadRequestedAt !== undefined && now - reloadRequestedAt < RELOAD_DEBOUNCE_MS) {
    return true;
  }
  const last = Number(storage.getItem(RELOAD_MARKER_KEY));
  if (last && now - last < RELOAD_DEBOUNCE_MS) {
    return false;
  }
  storage.setItem(RELOAD_MARKER_KEY, String(now));
  reloadRequestedAt = now;
  reload();
  return true;
}

// Listens for Vite's `vite:preloadError` (a chunk failed to load) and reloads
// to fetch the current index.html and fresh asset hashes.
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', () => {
    reloadForChunkError();
  });
}
